import type React from 'react';
import type { ComponentStateRuntime } from './componentStateRuntime';

/**
 * Loaded ESM module for a custom component. The artifact's default export is the React
 * component; other named exports are forwarded as-is in case the author re-exports utilities.
 */
export interface LoadedCustomComponentModule {
  default: unknown;
  [key: string]: unknown;
}

/**
 * Runtime handle the host app (editor canvas or live published page) hands to dynamically-loaded
 * custom components. The compiled artifact reads this directly off `globalThis.<MUTINY_HOST_RUNTIME_KEY>`
 * — no shim modules, no client-side rewriting.
 *
 *   - `React` is the host's singleton. Reusing it avoids dual-React hooks errors when the artifact
 *     mounts as a React subtree.
 *   - `loadComponent` resolves a sibling component (`import X from 'components/<slug>'` in the
 *     author's source) against the page's current manifest, returning the awaited module.
 */
export interface MutinyHostRuntime {
  React: typeof React;
  loadComponent: (slug: string) => Promise<LoadedCustomComponentModule>;
  /**
   * Diagram/animation library modules keyed by runtime slug (`visx-shape`, `gsap`, …), each the
   * library's ES module namespace. Populated lazily by `ensureRuntimeLibrariesRegistered()` the first
   * time an artifact references one — pages with no such component never load them.
   */
  runtimeLibraries?: Record<string, unknown>;
  state: ComponentStateRuntime;
}

export const MUTINY_HOST_RUNTIME_KEY = '__mutinyHostRuntime';

interface GlobalWithRuntime {
  [MUTINY_HOST_RUNTIME_KEY]?: MutinyHostRuntime;
}

// Preserve any already-registered `runtimeLibraries` across re-registration (the provider re-runs
// this when `loadComponent` changes) so a lazy library load isn't clobbered.
export function registerHostRuntime(runtime: MutinyHostRuntime): void {
  const g = globalThis as GlobalWithRuntime;
  g[MUTINY_HOST_RUNTIME_KEY] = {
    ...runtime,
    runtimeLibraries: runtime.runtimeLibraries ?? g[MUTINY_HOST_RUNTIME_KEY]?.runtimeLibraries,
  };
}

// Attach the lazily-loaded library modules onto the existing host runtime. Runs after
// `registerHostRuntime` (React + loadComponent are already present) per the loader's ordering.
export function registerRuntimeLibraries(modules: Record<string, unknown>): void {
  const g = globalThis as GlobalWithRuntime;
  const existing = g[MUTINY_HOST_RUNTIME_KEY];
  if (existing) {
    existing.runtimeLibraries = { ...existing.runtimeLibraries, ...modules };
  } else {
    throw new Error('Mutiny host runtime must be registered before runtime libraries.');
  }
}
